import type { HsHypersceneTypes } from '../types/hyperscene-types.type';

export class HsMatrix4 {
  public readonly values: HsHypersceneTypes.Array16;

  constructor(
    a00: number,
    a01: number,
    a02: number,
    a03: number,
    a10: number,
    a11: number,
    a12: number,
    a13: number,
    a20: number,
    a21: number,
    a22: number,
    a23: number,
    a30: number,
    a31: number,
    a32: number,
    a33: number
  ) {
    this.values = [a00, a01, a02, a03, a10, a11, a12, a13, a20, a21, a22, a23, a30, a31, a32, a33];
  }

  /**
   * Sets set.
   * @returns Result value.
   */
  public set(
    a00: number,
    a01: number,
    a02: number,
    a03: number,
    a10: number,
    a11: number,
    a12: number,
    a13: number,
    a20: number,
    a21: number,
    a22: number,
    a23: number,
    a30: number,
    a31: number,
    a32: number,
    a33: number
  ): HsMatrix4 {
    this.values[0] = a00;
    this.values[1] = a01;
    this.values[2] = a02;
    this.values[3] = a03;
    this.values[4] = a10;
    this.values[5] = a11;
    this.values[6] = a12;
    this.values[7] = a13;
    this.values[8] = a20;
    this.values[9] = a21;
    this.values[10] = a22;
    this.values[11] = a23;
    this.values[12] = a30;
    this.values[13] = a31;
    this.values[14] = a32;
    this.values[15] = a33;
    return this;
  }

  /**
   * Copies copy.
   * @param other other.
   * @returns Result value.
   */
  public copy(other: HsMatrix4): HsMatrix4 {
    this.values[0] = other.values[0];
    this.values[1] = other.values[1];
    this.values[2] = other.values[2];
    this.values[3] = other.values[3];
    this.values[4] = other.values[4];
    this.values[5] = other.values[5];
    this.values[6] = other.values[6];
    this.values[7] = other.values[7];
    this.values[8] = other.values[8];
    this.values[9] = other.values[9];
    this.values[10] = other.values[10];
    this.values[11] = other.values[11];
    this.values[12] = other.values[12];
    this.values[13] = other.values[13];
    this.values[14] = other.values[14];
    this.values[15] = other.values[15];
    return this;
  }

  /**
   * Copies position.
   * @param other other.
   * @returns Result value.
   */
  public copyPosition(other: HsMatrix4): HsMatrix4 {
    this.values[12] = other.values[12];
    this.values[13] = other.values[13];
    this.values[14] = other.values[14];

    return this;
  }

  /**
   * Copies rotation.
   * @param other other.
   * @returns Result value.
   */
  public copyRotation(other: HsMatrix4): HsMatrix4 {
    this.values[0] = other.values[0];
    this.values[1] = other.values[1];
    this.values[2] = other.values[2];

    this.values[4] = other.values[4];
    this.values[5] = other.values[5];
    this.values[6] = other.values[6];

    this.values[8] = other.values[8];
    this.values[9] = other.values[9];
    this.values[10] = other.values[10];

    return this;
  }

  /**
   * Handles negate rotation.
   * @returns Result value.
   */
  public negateRotation(): HsMatrix4 {
    this.values[0] = -this.values[0];
    this.values[1] = -this.values[1];
    this.values[2] = -this.values[2];

    this.values[4] = -this.values[4];
    this.values[5] = -this.values[5];
    this.values[6] = -this.values[6];

    this.values[8] = -this.values[8];
    this.values[9] = -this.values[9];
    this.values[10] = -this.values[10];

    return this;
  }

  /**
   * Sets from array.
   * @param array array.
   * @returns Result value.
   */
  public setFromArray(array: HsHypersceneTypes.Array16): HsMatrix4 {
    this.values[0] = array[0];
    this.values[1] = array[1];
    this.values[2] = array[2];
    this.values[3] = array[3];
    this.values[4] = array[4];
    this.values[5] = array[5];
    this.values[6] = array[6];
    this.values[7] = array[7];
    this.values[8] = array[8];
    this.values[9] = array[9];
    this.values[10] = array[10];
    this.values[11] = array[11];
    this.values[12] = array[12];
    this.values[13] = array[13];
    this.values[14] = array[14];
    this.values[15] = array[15];
    return this;
  }

  /**
   * Sets position.
   * @param offset offset.
   * @returns Result value.
   */
  public setPosition(offset: HsHypersceneTypes.Array3): HsMatrix4 {
    this.values[12] = offset[0];
    this.values[13] = offset[1];
    this.values[14] = offset[2];
    return this;
  }

  /**
   * Sets identity values.
   * @returns Result value.
   */
  public setIdentityValues(): HsMatrix4 {
    return this.set(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
  }

  /**
   * Handles multiply.
   * @param other other.
   * @returns Result value.
   */
  public multiply(other: HsMatrix4): HsMatrix4 {
    return this.multiplyMatrices(this, other);
  }

  /**
   * Handles premultiply.
   * @param other other.
   * @returns Result value.
   */
  public premultiply(other: HsMatrix4): HsMatrix4 {
    return this.multiplyMatrices(other, this);
  }

  /**
   * Handles multiply matrices.
   * @param first first.
   * @param second second.
   * @returns Result value.
   */
  public multiplyMatrices(first: HsMatrix4, second: HsMatrix4): HsMatrix4 {
    const a00: number = first.values[0];
    const a01: number = first.values[1];
    const a02: number = first.values[2];
    const a03: number = first.values[3];
    const a10: number = first.values[4];
    const a11: number = first.values[5];
    const a12: number = first.values[6];
    const a13: number = first.values[7];
    const a20: number = first.values[8];
    const a21: number = first.values[9];
    const a22: number = first.values[10];
    const a23: number = first.values[11];
    const a30: number = first.values[12];
    const a31: number = first.values[13];
    const a32: number = first.values[14];
    const a33: number = first.values[15];
    const b00: number = second.values[0];
    const b01: number = second.values[1];
    const b02: number = second.values[2];
    const b03: number = second.values[3];
    const b10: number = second.values[4];
    const b11: number = second.values[5];
    const b12: number = second.values[6];
    const b13: number = second.values[7];
    const b20: number = second.values[8];
    const b21: number = second.values[9];
    const b22: number = second.values[10];
    const b23: number = second.values[11];
    const b30: number = second.values[12];
    const b31: number = second.values[13];
    const b32: number = second.values[14];
    const b33: number = second.values[15];
    this.values[0] = b00 * a00 + b01 * a10 + b02 * a20 + b03 * a30;
    this.values[1] = b00 * a01 + b01 * a11 + b02 * a21 + b03 * a31;
    this.values[2] = b00 * a02 + b01 * a12 + b02 * a22 + b03 * a32;
    this.values[3] = b00 * a03 + b01 * a13 + b02 * a23 + b03 * a33;
    this.values[4] = b10 * a00 + b11 * a10 + b12 * a20 + b13 * a30;
    this.values[5] = b10 * a01 + b11 * a11 + b12 * a21 + b13 * a31;
    this.values[6] = b10 * a02 + b11 * a12 + b12 * a22 + b13 * a32;
    this.values[7] = b10 * a03 + b11 * a13 + b12 * a23 + b13 * a33;
    this.values[8] = b20 * a00 + b21 * a10 + b22 * a20 + b23 * a30;
    this.values[9] = b20 * a01 + b21 * a11 + b22 * a21 + b23 * a31;
    this.values[10] = b20 * a02 + b21 * a12 + b22 * a22 + b23 * a32;
    this.values[11] = b20 * a03 + b21 * a13 + b22 * a23 + b23 * a33;
    this.values[12] = b30 * a00 + b31 * a10 + b32 * a20 + b33 * a30;
    this.values[13] = b30 * a01 + b31 * a11 + b32 * a21 + b33 * a31;
    this.values[14] = b30 * a02 + b31 * a12 + b32 * a22 + b33 * a32;
    this.values[15] = b30 * a03 + b31 * a13 + b32 * a23 + b33 * a33;
    return this;
  }

  /**
   * Adds transition.
   * @param offset offset.
   * @returns Result value.
   */
  public addTransition(offset: HsHypersceneTypes.Array3): HsMatrix4 {
    this.values[12] = this.values[12] + offset[0];
    this.values[13] = this.values[13] + offset[1];
    this.values[14] = this.values[14] + offset[2];
    return this;
  }

  /**
   * Adds relative transition.
   * @param offset offset.
   * @returns Result value.
   */
  public addRelativeTransition(offset: HsHypersceneTypes.Array3): HsMatrix4 {
    const a00: number = this.values[0];
    const a01: number = this.values[1];
    const a02: number = this.values[2];
    const a03: number = this.values[3];
    const a10: number = this.values[4];
    const a11: number = this.values[5];
    const a12: number = this.values[6];
    const a13: number = this.values[7];
    const a20: number = this.values[8];
    const a21: number = this.values[9];
    const a22: number = this.values[10];
    const a23: number = this.values[11];
    const a30: number = this.values[12];
    const a31: number = this.values[13];
    const a32: number = this.values[14];
    const a33: number = this.values[15];
    this.values[12] = a00 * offset[0] + a10 * offset[1] + a20 * offset[2] + a30;
    this.values[13] = a01 * offset[0] + a11 * offset[1] + a21 * offset[2] + a31;
    this.values[14] = a02 * offset[0] + a12 * offset[1] + a22 * offset[2] + a32;
    this.values[15] = a03 * offset[0] + a13 * offset[1] + a23 * offset[2] + a33;
    return this;
  }

  /**
   * Handles scale.
   * @param size size.
   * @returns Result value.
   */
  public scale(size: HsHypersceneTypes.Array3): HsMatrix4 {
    this.values[0] *= size[0];
    this.values[1] *= size[0];
    this.values[2] *= size[0];
    this.values[3] *= size[0];
    this.values[4] *= size[1];
    this.values[5] *= size[1];
    this.values[6] *= size[1];
    this.values[7] *= size[1];
    this.values[8] *= size[2];
    this.values[9] *= size[2];
    this.values[10] *= size[2];
    this.values[11] *= size[2];
    return this;
  }

  /**
   * Gets scale.
   * @returns Result value.
   */
  public getScale(): HsHypersceneTypes.Array3 {
    const m00: number = this.values[0];
    const m01: number = this.values[1];
    const m02: number = this.values[2];
    const m10: number = this.values[4];
    const m11: number = this.values[5];
    const m12: number = this.values[6];
    const m20: number = this.values[8];
    const m21: number = this.values[9];
    const m22: number = this.values[10];
    const scaleX: number = Math.sqrt(m00 * m00 + m01 * m01 + m02 * m02);
    const scaleY: number = Math.sqrt(m10 * m10 + m11 * m11 + m12 * m12);
    const scaleZ: number = Math.sqrt(m20 * m20 + m21 * m21 + m22 * m22);
    return [scaleX, scaleY, scaleZ];
  }

  /**
   * Handles scale.
   * @returns Result value.
   */
  public resetScale(): HsMatrix4 {
    const scale: HsHypersceneTypes.Array3 = this.getScale();
    scale[0] = 1 / scale[0];
    scale[1] = 1 / scale[1];
    scale[2] = 1 / scale[2];
    this.scale(scale);
    return this;
  }

  /**
   * Handles inverse.
   * @returns Result value.
   */
  public inverse(): HsMatrix4 {
    const a00: number = this.values[0];
    const a01: number = this.values[1];
    const a02: number = this.values[2];
    const a03: number = this.values[3];
    const a10: number = this.values[4];
    const a11: number = this.values[5];
    const a12: number = this.values[6];
    const a13: number = this.values[7];
    const a20: number = this.values[8];
    const a21: number = this.values[9];
    const a22: number = this.values[10];
    const a23: number = this.values[11];
    const a30: number = this.values[12];
    const a31: number = this.values[13];
    const a32: number = this.values[14];
    const a33: number = this.values[15];
    const p0: number = a22 * a33;
    const p1: number = a32 * a23;
    const p2: number = a12 * a33;
    const p3: number = a32 * a13;
    const p4: number = a12 * a23;
    const p5: number = a22 * a13;
    const p6: number = a02 * a33;
    const p7: number = a32 * a03;
    const p8: number = a02 * a23;
    const p9: number = a22 * a03;
    const p10: number = a02 * a13;
    const p11: number = a12 * a03;
    const p12: number = a20 * a31;
    const p13: number = a30 * a21;
    const p14: number = a10 * a31;
    const p15: number = a30 * a11;
    const p16: number = a10 * a21;
    const p17: number = a20 * a11;
    const p18: number = a00 * a31;
    const p19: number = a30 * a01;
    const p20: number = a00 * a21;
    const p21: number = a20 * a01;
    const p22: number = a00 * a11;
    const p23: number = a10 * a01;
    const t0: number = p0 * a11 + p3 * a21 + p4 * a31 - (p1 * a11 + p2 * a21 + p5 * a31);
    const t1: number = p1 * a01 + p6 * a21 + p9 * a31 - (p0 * a01 + p7 * a21 + p8 * a31);
    const t2: number = p2 * a01 + p7 * a11 + p10 * a31 - (p3 * a01 + p6 * a11 + p11 * a31);
    const t3: number = p5 * a01 + p8 * a11 + p11 * a21 - (p4 * a01 + p9 * a11 + p10 * a21);
    const det: number = a00 * t0 + a10 * t1 + a20 * t2 + a30 * t3;
    if (det === 0) {
      this.set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
      return this;
    }
    const d: number = 1.0 / det;
    this.values[0] = d * t0;
    this.values[1] = d * t1;
    this.values[2] = d * t2;
    this.values[3] = d * t3;
    this.values[4] = d * (p1 * a10 + p2 * a20 + p5 * a30 - (p0 * a10 + p3 * a20 + p4 * a30));
    this.values[5] = d * (p0 * a00 + p7 * a20 + p8 * a30 - (p1 * a00 + p6 * a20 + p9 * a30));
    this.values[6] = d * (p3 * a00 + p6 * a10 + p11 * a30 - (p2 * a00 + p7 * a10 + p10 * a30));
    this.values[7] = d * (p4 * a00 + p9 * a10 + p10 * a20 - (p5 * a00 + p8 * a10 + p11 * a20));
    this.values[8] = d * (p12 * a13 + p15 * a23 + p16 * a33 - (p13 * a13 + p14 * a23 + p17 * a33));
    this.values[9] = d * (p13 * a03 + p18 * a23 + p21 * a33 - (p12 * a03 + p19 * a23 + p20 * a33));
    this.values[10] = d * (p14 * a03 + p19 * a13 + p22 * a33 - (p15 * a03 + p18 * a13 + p23 * a33));
    this.values[11] = d * (p17 * a03 + p20 * a13 + p23 * a23 - (p16 * a03 + p21 * a13 + p22 * a23));
    this.values[12] = d * (p14 * a22 + p17 * a32 + p13 * a12 - (p16 * a32 + p12 * a12 + p15 * a22));
    this.values[13] = d * (p20 * a32 + p12 * a02 + p19 * a22 - (p18 * a22 + p21 * a32 + p13 * a02));
    this.values[14] = d * (p18 * a12 + p23 * a32 + p15 * a02 - (p22 * a32 + p14 * a02 + p19 * a12));
    this.values[15] = d * (p22 * a22 + p16 * a02 + p21 * a12 - (p20 * a12 + p23 * a22 + p17 * a02));
    return this;
  }

  /**
   * Handles determinant.
   * @returns Result value.
   */
  public determinant(): number {
    const e11: number = this.values[0];
    const e12: number = this.values[4];
    const e13: number = this.values[8];
    const e14: number = this.values[12];
    const e21: number = this.values[1];
    const e22: number = this.values[5];
    const e23: number = this.values[9];
    const e24: number = this.values[13];
    const e31: number = this.values[2];
    const e32: number = this.values[6];
    const e33: number = this.values[10];
    const e34: number = this.values[14];
    const e41: number = this.values[3];
    const e42: number = this.values[7];
    const e43: number = this.values[11];
    const e44: number = this.values[15];
    return (
      e41 *
        (+e14 * e23 * e32 - e13 * e24 * e32 - e14 * e22 * e33 + e12 * e24 * e33 + e13 * e22 * e34 - e12 * e23 * e34) +
      e42 *
        (+e11 * e23 * e34 - e11 * e24 * e33 + e14 * e21 * e33 - e13 * e21 * e34 + e13 * e24 * e31 - e14 * e23 * e31) +
      e43 *
        (+e11 * e24 * e32 - e11 * e22 * e34 - e14 * e21 * e32 + e12 * e21 * e34 + e14 * e22 * e31 - e12 * e24 * e31) +
      e44 * (-e13 * e22 * e31 - e11 * e23 * e32 + e11 * e22 * e33 + e13 * e21 * e32 - e12 * e21 * e33 + e12 * e23 * e31)
    );
  }

  /**
   * Handles compose.
   * @param position position.
   * @param quaternion quaternion.
   * @param scale scale.
   * @returns Result value.
   */
  public compose(
    position: HsHypersceneTypes.Array3,
    quaternion: HsHypersceneTypes.Array4,
    scale: HsHypersceneTypes.Array3
  ): HsMatrix4 {
    const x: number = quaternion[0];
    const y: number = quaternion[1];
    const z: number = quaternion[2];
    const w: number = quaternion[3];
    const x2: number = x + x;
    const y2: number = y + y;
    const z2: number = z + z;
    const xx: number = x * x2;
    const xy: number = x * y2;
    const xz: number = x * z2;
    const yy: number = y * y2;
    const yz: number = y * z2;
    const zz: number = z * z2;
    const wx: number = w * x2;
    const wy: number = w * y2;
    const wz: number = w * z2;
    const sx: number = scale[0];
    const sy: number = scale[1];
    const sz: number = scale[2];

    this.values[0] = (1 - (yy + zz)) * sx;
    this.values[1] = (xy + wz) * sx;
    this.values[2] = (xz - wy) * sx;
    this.values[3] = 0;
    this.values[4] = (xy - wz) * sy;
    this.values[5] = (1 - (xx + zz)) * sy;
    this.values[6] = (yz + wx) * sy;
    this.values[7] = 0;
    this.values[8] = (xz + wy) * sz;
    this.values[9] = (yz - wx) * sz;
    this.values[10] = (1 - (xx + yy)) * sz;
    this.values[11] = 0;
    this.values[12] = position[0];
    this.values[13] = position[1];
    this.values[14] = position[2];
    this.values[15] = 1;

    return this;
  }

  /**
   * Handles subtract position.
   * @returns Result value.
   */
  public subtractPosition(offset: HsHypersceneTypes.Array3): HsMatrix4 {
    this.values[12] -= offset[0];
    this.values[13] -= offset[1];
    this.values[14] -= offset[2];
    return this;
  }

  /**
   * Gets position array.
   * @returns Result value.
   */
  public getPositionArray(): HsHypersceneTypes.Array3 {
    return [this.values[12], this.values[13], this.values[14]];
  }

  /**
   * Handles equals.
   * @param other other.
   * @param epsilon epsilon.
   * @returns Result value.
   */
  public equals(other: HsMatrix4, epsilon: number): boolean {
    if (Math.abs(this.values[0] - other.values[0]) > epsilon) return false;
    if (Math.abs(this.values[1] - other.values[1]) > epsilon) return false;
    if (Math.abs(this.values[2] - other.values[2]) > epsilon) return false;
    if (Math.abs(this.values[3] - other.values[3]) > epsilon) return false;
    if (Math.abs(this.values[4] - other.values[4]) > epsilon) return false;
    if (Math.abs(this.values[5] - other.values[5]) > epsilon) return false;
    if (Math.abs(this.values[6] - other.values[6]) > epsilon) return false;
    if (Math.abs(this.values[7] - other.values[7]) > epsilon) return false;
    if (Math.abs(this.values[8] - other.values[8]) > epsilon) return false;
    if (Math.abs(this.values[9] - other.values[9]) > epsilon) return false;
    if (Math.abs(this.values[10] - other.values[10]) > epsilon) return false;
    if (Math.abs(this.values[11] - other.values[11]) > epsilon) return false;
    if (Math.abs(this.values[12] - other.values[12]) > epsilon) return false;
    if (Math.abs(this.values[13] - other.values[13]) > epsilon) return false;
    if (Math.abs(this.values[14] - other.values[14]) > epsilon) return false;
    if (Math.abs(this.values[15] - other.values[15]) > epsilon) return false;
    return true;
  }

  /**
   * Gets position x.
   * @returns Result value.
   */
  public getPositionX(): number {
    return this.values[12];
  }

  /**
   * Gets position y.
   * @returns Result value.
   */
  public getPositionY(): number {
    return this.values[13];
  }

  /**
   * Gets position z.
   * @returns Result value.
   */
  public getPositionZ(): number {
    return this.values[14];
  }

  /**
   * Sets position x.
   * @returns Result value.
   */
  public setPositionX(value: number): HsMatrix4 {
    this.values[12] = value;
    return this;
  }

  /**
   * Sets position y.
   * @returns Result value.
   */
  public setPositionY(value: number): HsMatrix4 {
    this.values[13] = value;
    return this;
  }

  /**
   * Sets position z.
   * @returns Result value.
   */
  public setPositionZ(value: number): HsMatrix4 {
    this.values[14] = value;
    return this;
  }

  /**
   * Handles distance.
   * @param other other.
   * @returns Result value.
   */
  public distance(other: HsMatrix4): number {
    const dx: number = this.values[12] - other.values[12];
    const dy: number = this.values[13] - other.values[13];
    const dz: number = this.values[14] - other.values[14];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  public rotateX(angle: number): HsMatrix4 {
    const c: number = Math.cos(angle);
    const s: number = Math.sin(angle);
    const a10: number = this.values[4];
    const a11: number = this.values[5];
    const a12: number = this.values[6];
    const a13: number = this.values[7];
    const a20: number = this.values[8];
    const a21: number = this.values[9];
    const a22: number = this.values[10];
    const a23: number = this.values[11];
    this.values[4] = a10 * c + a20 * s;
    this.values[5] = a11 * c + a21 * s;
    this.values[6] = a12 * c + a22 * s;
    this.values[7] = a13 * c + a23 * s;
    this.values[8] = a20 * c - a10 * s;
    this.values[9] = a21 * c - a11 * s;
    this.values[10] = a22 * c - a12 * s;
    this.values[11] = a23 * c - a13 * s;
    return this;
  }

  /**
   * Builds identity matrix.
   * @returns Result value.
   */
  public static makeIdentityMatrix(): HsMatrix4 {
    return new HsMatrix4(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
  }

  /**
   * Builds copy.
   * @returns Result value.
   */
  public static makeCopy(other: HsMatrix4): HsMatrix4 {
    return new HsMatrix4(...other.values);
  }

  /**
   * Builds perspective matrix.
   * @param fov fov.
   * @param aspect aspect.
   * @param near near.
   * @param far far.
   * @returns Result value.
   */
  public static makePerspectiveMatrix(fov: number, aspect: number, near: number, far: number): HsMatrix4 {
    const rangeInverted: number = 1 / (near - far);
    const a11: number = Math.tan(Math.PI * 0.5 - 0.5 * fov);
    const a00: number = a11 / aspect;
    const a22: number = (near + far) * rangeInverted;
    const a32: number = near * far * rangeInverted * 2;
    return new HsMatrix4(a00, 0, 0, 0, 0, a11, 0, 0, 0, 0, a22, -1, 0, 0, a32, 0);
  }

  /**
   * Builds orthographic matrix.
   * @param left left.
   * @param right right.
   * @param bottom bottom.
   * @param top top.
   * @param near near.
   * @param far far.
   * @returns Result value.
   */
  public static makeOrthographicMatrix(
    left: number,
    right: number,
    bottom: number,
    top: number,
    near: number,
    far: number
  ): HsMatrix4 {
    const a00: number = 2 / (right - left);
    const a11: number = 2 / (top - bottom);
    const a22: number = 2 / (near - far);
    const a30: number = (left + right) / (left - right);
    const a31: number = (bottom + top) / (bottom - top);
    const a32: number = (near + far) / (near - far);
    return new HsMatrix4(a00, 0, 0, 0, 0, a11, 0, 0, 0, 0, a22, 0, a30, a31, a32, 1);
  }

  /**
   * Gets max scale on axis.
   * @param matrix matrix.
   * @returns Result value.
   */
  public static getMaxScaleOnAxis(matrix: HsMatrix4): number {
    const e: HsHypersceneTypes.Array16 = matrix.values;
    const scaleXSquare: number = e[0] * e[0] + e[1] * e[1] + e[2] * e[2];
    const scaleYSquare: number = e[4] * e[4] + e[5] * e[5] + e[6] * e[6];
    const scaleZSquare: number = e[8] * e[8] + e[9] * e[9] + e[10] * e[10];
    return Math.sqrt(Math.max(scaleXSquare, scaleYSquare, scaleZSquare));
  }

  /**
   * Handles compose.
   * @param position position.
   * @param quaternion quaternion.
   * @param scale scale.
   * @returns Result value.
   */
  public static compose(
    position: HsHypersceneTypes.Array3,
    quaternion: HsHypersceneTypes.Array4,
    scale: HsHypersceneTypes.Array3
  ): HsMatrix4 {
    return HsMatrix4.makeIdentityMatrix().compose(position, quaternion, scale);
  }
}
